import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "À propos", id: "apropos" },
  { label: "Services", id: "services" },
  { label: "Réalisations", id: "portfolio" },
  { label: "Secteurs", id: "secteurs" },
  { label: "Méthode", id: "methode" },
  { label: "FAQ", id: "faq" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => scrollTo("hero")}
            className="text-left leading-none"
          >
            <span className="font-serif text-2xl tracking-tight text-[#F5F5F0]">VF</span>
            <span className="font-serif text-2xl tracking-tight gold-text"> Création</span>
            <span className="block eyebrow mt-0.5 text-[9px]">Vidéo Immersive</span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="link-underline text-sm text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="nav-devis-btn"
              onClick={() => scrollTo("devis")}
              className="hidden sm:inline-flex items-center border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 rounded-full px-5 py-2 text-sm tracking-wide"
            >
              Demande de devis
            </button>
            <button
              data-testid="nav-menu-toggle"
              className="lg:hidden text-[#F5F5F0]"
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-lg lg:hidden flex flex-col"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
              <span className="font-serif text-2xl gold-text">VF Création</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#F5F5F0]">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-6 px-8">
              {[...LINKS, { label: "Contact", id: "contact" }].map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="font-serif text-4xl text-left text-[#F5F5F0]"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="mobile-devis-btn"
                onClick={() => { scrollTo("devis"); setOpen(false); }}
                className="mt-6 border border-[#D4AF37] text-[#D4AF37] rounded-full px-6 py-3 text-sm tracking-wide self-start"
              >
                Demande de devis
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
